import React, {useEffect, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import Icon from "../../../system/components/icon";
import {useTranslate} from "../../../system/useTranslate";
import {translateCrmRequestError} from "../../../models/crm/crm-error";

export const initialCustomerFormValue = {
    customerType: "person",
    status: "active",
    displayName: "",
    firstName: "",
    lastName: "",
    companyName: "",
    jobTitle: "",
    primaryPhone: "",
    secondaryPhone: "",
    primaryEmail: "",
    secondaryEmail: "",
    website: "",
    externalId: "",
    address: "",
    description: "",
};

function addressText(address) {
    if (!address) return "";
    if (typeof address === "string") return address;
    return address.formatted || address.fullAddress || address.address || JSON.stringify(address);
}

export function customerFormValue(customer = {}) {
    const value = Object.fromEntries(Object.entries(initialCustomerFormValue).map(([field, fallback]) => [field, customer?.[field] ?? fallback]));
    return {...value, address: addressText(customer?.address), _addressOriginal: customer?.address || null};
}

export function customerWritePayload(value = {}) {
    const payload = Object.fromEntries(Object.keys(initialCustomerFormValue)
        .filter((field) => field !== "address")
        .map((field) => [field, value?.[field] ?? initialCustomerFormValue[field]]));
    const address = String(value?.address || "").trim();
    const originalAddress = value?._addressOriginal;
    const normalizedAddress = originalAddress && address === addressText(originalAddress).trim()
        ? originalAddress
        : address ? {formatted: address} : null;
    return {...payload, address: normalizedAddress};
}

function Header({customer}) {
    const translate = useTranslate();
    return <div className="flex flex-column">
        <span className="text-lg">{translate(customer ? "crm.customer.edit" : "crm.customer.add")}</span>
        <span className="text-xs text-400">{customer?.displayName || translate("crm.customers.description")}</span>
    </div>;
}

function Footer({onHide, onSubmit, submitting}) {
    const translate = useTranslate();
    return <div className="flex flex-row-reverse align-items-center gap-2 justify-content-start p-3 surface-50 border-round-bottom-2xl border-top-1 border-300">
        <Button icon={<Icon icon="mi mi:save" className="text-lg"/>} className="text-xs flex gap-2 border-round-lg p-2" label={translate("save-changes")} loading={submitting} disabled={submitting} onClick={onSubmit}/>
        <Button className="text-xs p-button-secondary p-button-outlined border-round-lg p-2" label={translate("cancel")} disabled={submitting} onClick={onHide}/>
    </div>;
}

function Field({id, label, children, className = "col-12 md:col-6"}) {
    return <div className={`${className} flex flex-column gap-1`}>
        <label className="text-xs" htmlFor={id}>{label}</label>
        {children}
    </div>;
}

export default function CustomerDialog({visible, onHide, onSubmit, initialCustomer}) {
    const translate = useTranslate();
    const [value, setValue] = useState(initialCustomerFormValue);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!visible) return;
        setValue(customerFormValue(initialCustomer));
        setError("");
        setLoading(false);
    }, [initialCustomer, visible]);

    const change = (field, next) => setValue((current) => ({...current, [field]: next}));
    const dropdownPt = {input: () => ({className: "text-xs"}), item: () => ({className: "text-xs"})};
    const submit = async () => {
        if (!value.displayName.trim()) {
            setError(translate("crm.customer.validation.name"));
            return;
        }
        setLoading(true);
        setError("");
        try {
            await onSubmit(customerWritePayload(value));
            onHide();
        } catch (requestError) {
            setError(translateCrmRequestError(translate, requestError, "crm.error.save"));
        } finally {
            setLoading(false);
        }
    };

    return <Dialog
        pt={{footer: () => ({className: "p-0 border-round-bottom-2xl"}), content: () => ({className: "p-0 h-full"})}}
        header={<Header customer={initialCustomer}/>} contentClassName="p-4"
        headerClassName="border-round-top-2xl border-bottom-1 border-300 pb-3 pt-3"
        footer={<Footer onHide={onHide} onSubmit={submit} submitting={loading}/>} className="rtl w-11 md:w-8 lg:w-6 border-round-bottom-2xl"
        visible={visible} onHide={() => {if (!loading) onHide();}} modal>
        <div className="flex flex-column gap-3">
            {error && <Message className="text-xs" severity="error" text={error}/>} 
            <div className="grid">
                <Field id="crm-customer-type" label={translate("crm.customer.type")}>
                    <Dropdown inputId="crm-customer-type" value={value.customerType} onChange={(event) => change("customerType", event.value)} options={[{label: translate("crm.customer.person"), value: "person"}, {label: translate("crm.customer.company"), value: "company"}]} className="w-full text-xs" panelClassName="rtl text-xs" pt={dropdownPt}/>
                </Field>
                <Field id="crm-customer-status" label={translate("crm.customer.status")}>
                    <Dropdown inputId="crm-customer-status" value={value.status} onChange={(event) => change("status", event.value)} options={[{label: translate("crm.customer.active"), value: "active"}, {label: translate("crm.customer.inactive"), value: "inactive"}]} className="w-full text-xs" panelClassName="rtl text-xs" pt={dropdownPt}/>
                </Field>
                <Field id="crm-customer-name" label={translate("crm.customer.name")} className="col-12">
                    <InputText id="crm-customer-name" value={value.displayName} onChange={(event) => change("displayName", event.target.value)} className="w-full text-xs" autoFocus/>
                </Field>
                <Field id="crm-customer-first-name" label={translate("crm.customer.first-name")}><InputText id="crm-customer-first-name" value={value.firstName} onChange={(event) => change("firstName", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-last-name" label={translate("crm.customer.last-name")}><InputText id="crm-customer-last-name" value={value.lastName} onChange={(event) => change("lastName", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-company" label={translate("crm.customer.company-name")}><InputText id="crm-customer-company" value={value.companyName} onChange={(event) => change("companyName", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-job" label={translate("crm.customer.job-title")}><InputText id="crm-customer-job" value={value.jobTitle} onChange={(event) => change("jobTitle", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-phone" label={translate("crm.customer.phone")}><InputText id="crm-customer-phone" value={value.primaryPhone} onChange={(event) => change("primaryPhone", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-secondary-phone" label={translate("crm.customer.secondary-phone")}><InputText id="crm-customer-secondary-phone" value={value.secondaryPhone} onChange={(event) => change("secondaryPhone", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-email" label={translate("crm.customer.email")}><InputText id="crm-customer-email" value={value.primaryEmail} onChange={(event) => change("primaryEmail", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-secondary-email" label={translate("crm.customer.secondary-email")}><InputText id="crm-customer-secondary-email" value={value.secondaryEmail} onChange={(event) => change("secondaryEmail", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-website" label={translate("crm.customer.website")}><InputText id="crm-customer-website" value={value.website} onChange={(event) => change("website", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-external-id" label={translate("crm.customer.external-id")}><InputText id="crm-customer-external-id" value={value.externalId} onChange={(event) => change("externalId", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-address" label={translate("crm.customer.address")} className="col-12"><InputTextarea id="crm-customer-address" rows={2} value={value.address} onChange={(event) => change("address", event.target.value)} className="w-full text-xs"/></Field>
                <Field id="crm-customer-description" label={translate("crm.customer.description")} className="col-12"><InputTextarea id="crm-customer-description" rows={4} value={value.description} onChange={(event) => change("description", event.target.value)} className="w-full text-xs"/></Field>
            </div>
        </div>
    </Dialog>;
}
